import { styled } from '@mui/material/styles';
import { Box, Button, TextareaAutosize, Typography } from '@mui/material';

export const HeaderContainer = styled(Box)({
  position: 'relative',
});

export const HeaderContent = styled(Box)({
  marginLeft: '36px',
  marginTop: '40px',
  display: 'flex',
  alignItems: 'center',
});

export const AlertIcon = styled(Box)({
  fontSize: '24px',
  color: '#0F52BA',
  marginRight: '16px',
});

export const HeaderTitle = styled(Typography)({
  fontFamily: 'Lufga',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '24px',
  lineHeight: '100%',
  color: '#0B2330',
});

export const HeaderDivider = styled(Box)({
  borderBottom: '1px solid #D9F1FF',
  marginTop: '24px',
  marginLeft: '38px',
  marginRight: '38px',
});

export const ScrollableContent = styled(Box)({
  maxHeight: '370px', // 538px dialog height - 100px header area - 96px button area
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '8px',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'var(--loader-color)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
});

export const FormContainer = styled(Box)({
  padding: '22px 38px 24px 38px',
});

export const FieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  position: 'relative',
  paddingBottom: '3px',
});

export const FieldLabel = styled(Box)({
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '100%',
  color: '#555555',
});

export const RadioGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  marginTop: '12px',
});

export const RadioOption = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  boxSizing: 'border-box',
  width: '166px',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '8px',
  background: '#FCFEFF',
  border: selected ? '1px solid #D9F1FF' : '1px solid #DFDFDF',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: '#D9F1FF',
  },
}));

export const RadioCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: selected ? 'none' : '2px solid #75838A',
  background: selected ? '#0F52BA' : 'transparent',
  marginRight: '12px',
  '&::after': selected
    ? {
        content: '"✓"',
        color: '#FFFFFF',
        fontSize: '12px',
        fontWeight: 'bold',
      }
    : {},
}));

export const RadioLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: selected ? '#0F52BA' : '#75838A',
}));

export const FixedButtonContainer = styled(Box)({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: '8px 36px 30px 36px',
  background: '#FFFFFF',
  borderTop: '1px solid #D9F1FF',
  display: 'flex',
  gap: '7px',
});

export const UpdateButton = styled(Button)({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '12px 24px',
  width: '102px',
  height: '40px',
  background: '#0F52BA',
  border: '1px solid #0D4AA9',
  boxShadow: '0px 2px 4px #E2E3E1',
  borderRadius: '8px',
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '100%',
  color: '#FFFFFF',
  textTransform: 'none',
  '&:hover': {
    background: '#0D4AA9',
  },
  '&:disabled': {
    background: '#CCCCCC',
    border: '1px solid #AAAAAA',
  },
});

export const CancelButton = styled(Button)({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '12px 24px',
  width: '97px',
  height: '40px',
  background: '#F1F7FF',
  border: '1px solid #DFDFDF',
  boxShadow: '0px 2px 4px rgba(217, 241, 255, 0.6)',
  borderRadius: '8px',
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '100%',
  color: '#75838A',
  textTransform: 'none',
  '&:hover': {
    background: '#E8F2FF',
  },
});

export const DeliveryNoteContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const DeliveryNoteTextArea = styled(TextareaAutosize)({
  boxSizing: 'border-box',
  width: '648px',
  minHeight: '112px',
  maxHeight: '112px !important',
  height: '112px !important',
  padding: '16px',
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '1.5',
  color: '#75838A',
  background: '#FCFEFF',
  border: '1px solid #D9F1FF',
  borderRadius: '8px',
  resize: 'none',
  overflow: 'auto !important',
  '&:focus': {
    outline: 'none',
    borderColor: '#0F52BA',
  },
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'var(--bg-color)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'var(--loader-color)',
    borderRadius: '4px',
    height: '25px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'var(--primary-blue)',
    cursor: 'pointer',
  },
});

export const ErrorMessage = styled(Box)({
  position: 'absolute',
  top: 'calc(100% - 25px)',
  left: 0,
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#E91E63',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const DialogContainer = styled(Box)({
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const FirstFieldGroup = styled(FieldGroup)({
  marginBottom: '0px',
});

export const DeliveryNoteFieldContainer = styled(DeliveryNoteContainer)({
  marginTop: '16px',
  marginBottom: '16px',
});

export const DeliveryStatusContainer = styled(Box)({
  marginTop: '16px',
});

export const SmallValue14 = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  color: '#0B2330',
  fontWeight: 400,
});

export const ActualDeliveryFieldGroup = styled(FieldGroup)({
  marginTop: '16px',
  paddingBottom: '5px',
});

export const DateLabel = styled(Box)({
  marginTop: '8px',
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: '#75838A',
});

export const DeliveryNoteLabel = styled(Box)({
  marginTop: '8px',
  fontFamily: 'Outfit',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '1.5',
  color: '#75838A',
  maxWidth: '648px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

export default {};
